import { useMemo } from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import type { AxiosResponse } from 'axios';

import {
  listAutomationRevisionSummaries,
  getListAutomationRevisionSummariesQueryKey,
} from '@/generated/hooks/grimoire/v1/api';
import type { V1ListAutomationRevisionSummariesResponse } from '@/generated/hooks/grimoire/models/v1ListAutomationRevisionSummariesResponse';
import {
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
  useUserPreferencesStore,
} from '@/shared/stores/user-preferences.store';
import { getRevisionId } from '@/shared/utils';

/** Number of revisions to fetch per page for the version picker. */
const REVISIONS_PAGE_SIZE = 10;

/**
 * Return type for {@link useAutomationRevisions}.
 */
export interface IUseAutomationRevisionsReturn {
  /** Revision version strings (e.g. "0.1", "12.0"), newest first. */
  versions: string[];
  /**
   * The subset of {@link versions} that are published. A revision is considered
   * published when its snapshot carries a `publish_time` (the authoritative
   * server-side marker), rather than inferring it from the version string.
   */
  publishedVersionIds: Set<string>;
  /** Whether the first page of revisions is loading. */
  isLoading: boolean;
  /**
   * Whether the revision list failed with nothing to show. The query does not
   * retry, so consumers must surface this — otherwise a failure is
   * indistinguishable from an automation that has no revisions.
   *
   * Backed by react-query's `isLoadingError` (`isError && !hasData`) rather
   * than `isError`, so it stays scoped to "failed with no data" even if a
   * later page's failure starts reaching query state. As of react-query
   * 5.85 a failed `fetchNextPage` is not reflected in the hook's state at
   * all (status stays `success`), so today the two agree.
   */
  isLoadError: boolean;
  /** Whether more pages are available. */
  hasNextPage: boolean;
  /** Whether the next page is being fetched. */
  isFetchingNextPage: boolean;
  /** Fetches the next page of revisions. */
  fetchNextPage: () => void;
  /**
   * Refetches from the first page. The query does not retry on its own, so
   * this is the user's recovery path out of {@link isLoadError}.
   */
  refetch: () => void;
}

/**
 * Lists all revisions (versions) of an automation in the current workspace, for
 * use in a version picker. Both draft and published revisions are returned
 * (newest first), so this works for either an published automation or a draft.
 * Shared by the Export and Move dialogs.
 *
 * @param automationId - The automation whose revisions to list
 * @param enabled - Whether to run the query (e.g. only when a dialog is open)
 * @returns Version strings and pagination state
 */
export function useAutomationRevisions(
  automationId: string,
  enabled: boolean,
): IUseAutomationRevisionsReturn {
  const currentOrganizationId = useUserPreferencesStore(
    selectCurrentOrganizationId,
  );
  const currentWorkspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Reads revision summaries, not the full-revision list. Each summary's
  // snapshot omits the heavy `code` (SPy) field and `description`, which —
  // together with the small page size — keeps the response under the API
  // gateway's gRPC-JSON transcoding limit. The full-revision list returns
  // multi-MB pages and 500s for automations with large code (MAN-3895). Only
  // `name` and `snapshot.publish_time` are read below, both on the summary.
  const {
    data: revisionsData,
    isLoading,
    isLoadingError,
    fetchNextPage,
    refetch,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery<
    AxiosResponse<V1ListAutomationRevisionSummariesResponse>,
    Error,
    {
      pages: AxiosResponse<V1ListAutomationRevisionSummariesResponse>[];
      pageParams: (string | undefined)[];
    },
    ReturnType<typeof getListAutomationRevisionSummariesQueryKey>,
    string | undefined
  >({
    queryKey: getListAutomationRevisionSummariesQueryKey(
      currentOrganizationId,
      currentWorkspaceId,
      automationId,
      { page_size: REVISIONS_PAGE_SIZE },
    ),
    queryFn: ({ pageParam, signal }) =>
      listAutomationRevisionSummaries(
        currentOrganizationId,
        currentWorkspaceId,
        automationId,
        {
          page_size: REVISIONS_PAGE_SIZE,
          page_token: pageParam,
        },
        { signal },
      ),
    enabled: Boolean(
      enabled && currentOrganizationId && currentWorkspaceId && automationId,
    ),
    initialPageParam: undefined,
    // A transcoding failure here is deterministic for a given automation, so
    // the default 3 retries only re-request the same payload.
    retry: false,
    getNextPageParam: lastPage => lastPage.data.next_page_token || undefined,
  });

  const { versions, publishedVersionIds } = useMemo(() => {
    const revisions =
      revisionsData?.pages.flatMap(
        page => page.data.automation_revision_summaries ?? [],
      ) ?? [];
    const ids: string[] = [];
    const published = new Set<string>();
    for (const revision of revisions) {
      const id = getRevisionId(revision.name);
      if (!id) continue;
      ids.push(id);
      // A revision is published once it has a publish_time on its snapshot.
      if (revision.snapshot?.publish_time) {
        published.add(id);
      }
    }
    return { versions: ids, publishedVersionIds: published };
  }, [revisionsData]);

  return {
    versions,
    publishedVersionIds,
    isLoading,
    isLoadError: isLoadingError,
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    fetchNextPage,
    // Discard the click/refetch argument: react-query treats the first
    // parameter as RefetchOptions, and a DOM event is not that.
    refetch: () => void refetch(),
  };
}
